<template>
<main class="has-breadcrumbs">
    <a id="main-content" class="visually-hidden" tabindex="-1"></a>
    <div v-if="isLoading">
        <Loading />
    </div>

    <section class="outer-container--grey" v-bind:class="{'short-header': hasSearched}">
        <SectionHeading :title="i18n_strings.human_rights_resources" :hide="hasSearched" />

        <div class="container outer-container--last-in-shaded-group search-box" v-bind:class="{'short-header': hasSearched}"
            role="search">
            <SearchIcon/>
            <label for="searchbox" class="visually-hidden">{{ i18n_strings.search_for }}:</label>
            <input id="searchbox"
                class="form-text text-input search-input-underlined"
                ref="searchBox"
                type="text"
                v-on:change="searchQueryChanged"
                v-on:keyup.enter="searchQueryChanged"
                :value="searchQuery"
                :aria-describedby="resultsCount === 0 ? 'results-quantity-text' : ''"
                :placeholder="i18n_strings.search_placeholder"/>
            <button :aria-label="i18n_strings.aria_clear_search" class="clear-search-button" v-show="queryExists" v-on:click="clearSearchQuery">
                <XIcon/>
            </button>
        </div>
    </section>

    <div class="container container--reset-padding">
        <div class="search-grid">
            <section class="results-quantity" :aria-label="i18n_strings.aria_total_search">
                <h2 class="heading-3 heading-6">
                    <span id="results-quantity-text" aria-live="polite">{{this.resultsCountText}}</span>
                </h2>
            </section>
            <section :aria-label="i18n_strings.aria_current_facets" v-if="searchSelectedFacets" class="selected-facets">
                <ul :aria-label="i18n_strings.aria_applied_facets" class="selected-facets-list">
                    <li class="selected-facet"
                        v-for="(facetInfo, index) in searchSelectedFacets"
                        v-bind:key="index">
                        <p class="selected-facet-name vertically-challenged-selected-facet">
                            {{ decodeURI(facetInfo.facet) }}
                        </p>
                        <button class="remove-facet-button"
                                v-on:click="removeFacet(facetInfo)"
                                :aria-label="`${i18n_strings.aria_deselect} ${facetInfo.category} ${decodeURI(facetInfo.facet)}`">
                            <PlusIcon/>
                        </button>
                    </li>
                </ul>
            </section>
            <div class="facet-lists">
                <div class="facets-header">
                    <button class="toggle-header-button js-dropdown-label button cb-button down-arrow"
                            v-on:click="toggleFacetLists"
                            role="button"
                            :aria-expanded="`${facetsDisplayed}`"
                            aria-controls="facets"
                            aria-label="filter by">
                        <p v-bind:class="{'facetsDisplayed': facetsDisplayed}" role="heading" aria-level="2">{{i18n_strings.filter}}</p>
                    </button>
                </div>
                <div class="facets" v-bind:class="{'facetsDisplayed': facetsDisplayed}">
                    <FacetList :facetName="i18n_strings.collection"
                        facetKey="collections"
                        :facetOptions="searchFacets.collections"
                        v-on:addFacet="addFacet"
                        v-on:removeFacet="removeFacet"/>

                    <FacetList :facetName="i18n_strings.education_level"
                        facetKey="education_levels"
                        :facetOptions="searchFacets.education_levels"
                        v-on:addFacet="addFacet"
                        v-on:removeFacet="removeFacet"/>

                    <FacetList :facetName="i18n_strings.keyword"
                        facetKey="topics"
                        :facetOptions="searchFacets.topics"
                        v-on:addFacet="addFacet"
                        v-on:removeFacet="removeFacet"/>
                </div>
            </div>
            <section :aria-label="i18n_strings.aria_search_filters" class="filters filter-bar">
                <FilterDropdown filter="languages" :searchSelectedFilters="searchSelectedFilters" :optionType="i18n_strings.all_languages" :options="contentLanguages" :listLabel="i18n_strings.language" :autoLang="autoLang" v-on:kill-auto-lang="killAutoLang"/>
                <FilterDropdown filter="formats" :searchSelectedFilters="searchSelectedFilters"  :optionType="i18n_strings.all_formats" :options="contentFormats" :listLabel="i18n_strings.format" />
            </section>
            <section class="results">
                <ul class="results-list" :aria-label="i18n_strings.aria_search_results">
                    <li v-for="result in searchResultsCmhr" v-bind:key="result.id">
                        <SearchResult :result="result" />
                    </li>
                    <li v-for="result in searchResults" v-bind:key="result.id">
                        <SearchResult :result="result" />
                    </li>
                </ul>
            </section>
        </div>
    </div>

    <section class="page-buttons" role="navigation" :aria-label="i18n_strings.aria_page_nav">
        <button class="button no-border left-arrow" :aria-label="i18n_strings.aria_prev"
            v-if="searchPreviousPage" v-on:click="previousPage">
        </button>
        <button class="button no-border" :aria-label="i18n_strings.aria_next"
            v-if="searchNextPage" v-on:click="nextPage">
        </button>
    </section>

    <Verified :numResults="resultsCount" />
</main>
</template>


<style lang="scss">
.outer-container--grey.short-header {
    padding-top: 2rem;
}

.search-box {
    &.short-header {
        padding-bottom: 3.125rem;
    }

    .clear-search-button {
        right: 53px;
        height: 40px;
        width: 40px;

        border: none;
        background: none;

        &:focus {
            transition: none;
            outline: .0625rem dashed #000;
            outline-offset: .3125rem;
        }
    }

    @media screen and (max-width: 600px) {
        &.short-header {
            padding-bottom: 1.125rem;
        }
        .clear-search-button {
            top: 15px;
            right: 15px;
        }
    }
}

.search-grid {
    width: 100%;
    margin-top: 1.5rem;
    display: -ms-grid;
    display: grid;
    -ms-grid-columns: [facets] 30% [results] 70%;
    grid-template-columns: [facets] 30% [results] 70%;
    -ms-grid-rows: [filters] auto [active-facets] auto [facets-results] auto;
    grid-template-rows: [filters] auto [active-facets] auto [facets-results] auto;

    .results-quantity {
        -ms-grid-column: 1;
        grid-column-start: facets;
        grid-column-end: none;
        -ms-grid-column-span: none;
        -ms-grid-row: 1;
        -ms-grid-row-span: 1;
        grid-row-start: filters;
        display: flex;
        align-items: center;
        width: 40%;

        .heading-3 {
            margin-bottom: 0;
            font-size: 1.4rem;
        }
    }

    .filter-bar {
        padding: 0;
    }

    .filters {
        grid-column-start: results;
        -ms-grid-column: 2;
        -ms-grid-column-span: 1;
        grid-row-start: filters;
        -ms-grid-row: 1;
        -ms-grid-row-span: 1;
        justify-content: flex-end;
        border: none;

        .dropdown {
            width: 14rem;
        }
    }


    .selected-facets {
        grid-column-start: facets;
        -ms-grid-column: 1;
        -ms-grid-column-span: 2;
        grid-column-end: 3;
        grid-row-start: active-facets;
        -ms-grid-row: 2;
        -ms-grid-row-span: 1;

        .selected-facets-list {
            display: flex;
            justify-content: flex-start;
            padding-left: 0;
            flex-wrap: wrap;

            .selected-facet {
                height: 30px;
                font-size: .9rem;
                background-color: #bddbe5;
                list-style: none;
                margin: .3rem .4rem .3rem 0;
                border-radius: 5px;
                padding: .2rem .9rem;
                display: flex;
                align-items: center;
                justify-content: space-between;

                .selected-facet-name {
                    display: inline-block;
                    margin: 0 10px 0 0;
                }

                .remove-facet-button {
                    background: none;
                    border: none;
                    padding: 0;
                    height: 13px;
                    width: 13px;

                    .plus-icon {
                        height: 13px;
                        width: 13px;
                        transform: rotate(45deg);
                    }

                    &:focus {
                        transition: none;
                        outline: .0625rem dashed #000;
                        outline-offset: .3125rem;
                    }
                }
            }
        }
    }

    .facet-lists {
        width: 80%;
        grid-column-start: facets;
        -ms-grid-column: 1;
        -ms-grid-column-span: 1;
        grid-row-start: facets-results;
        -ms-grid-row: 3;
        -ms-grid-row-span: 1;

        .facets-header {
            display: none;

            button {
                position: relative;
                text-align: left;
                padding: .5rem 1.5rem;
                width: 100%;
                outline: none;
                border: none;

                &.down-arrow::after {
                    content: none!important;
                }

                p {
                    margin: 0;

                    &::after {
                        position: absolute;
                        top: 50%;
                        right: .8125rem;
                        left: auto;
                        display: block;
                        width: 0;
                        height: 0;
                        content: "";
                        transition: right .2s ease;
                        -webkit-transform: translateY(-50%);
                        transform: translateY(-50%);
                        border-top: .375rem solid #232323;
                        border-right: .375rem solid transparent;
                        border-left: .375rem solid transparent;
                    }

                    &.facetsDisplayed::after {
                        transform: rotate(180deg);
                    }
                }

                &:hover p::after {
                    border-top-color: #fff;
                    border-right-color: transparent;
                    border-left-color: transparent;
                }

                &:focus p::after {
                    outline: .0625rem dashed #000;
                    outline-offset: .3125rem;
                }

                &:hover:focus {
                    p::after {
                        outline: .0625rem dashed #fff;
                    }
                }
            }
        }

        .facets {
            &.facetsDisplayed {
                display: block;
            }

        }
    }

    .results {
        grid-column-start: results;
        -ms-grid-column: 2;
        -ms-grid-column-span: 1;
        grid-row-start: facets-results;
        -ms-grid-row: 3;
        -ms-grid-row-span: 1;
    }
}

@media screen and (max-width: 900px) {
    .search-grid {
        display: flex;
        flex-direction: column;

        .results-quantity {
            width: 100%;
        }

        .selected-facets {
            order: 2;
        }

        .facet-lists {
            order: 3;
            width: 100%;
            border: .125rem solid #232323;
            margin-bottom: .7rem;


            .facets-header {
                display: block;
            }

            .facets {
                display: none;
            }
        }

        .filters {
            order: 4;

            .dropdown {
                margin-bottom: 0;
                width: 100%;
            }
        }

        .results {
            order: 5;
        }
    }
}

.page-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem auto 4rem;
    width: 50%;

    button.no-border {
        width: 2rem;
        margin: .5rem;
        padding: .25rem .25rem;
        border: none;

        &.left-arrow {
            transform: rotate(-180deg);
        }

        &:hover {
            background-color: white;

            &::after {
                border-color: black;
            }
        }

        &::after {
            border-color: black;
        }

    }
}
</style>


<script>
import { mapGetters } from "vuex";
import {
    SEARCH,
    SEARCH_PAGE_NEXT,
    SEARCH_PAGE_PREV
} from "@/store/actions.type";

import Loading from "@/components/Loading";
import SearchResult from "@/components/search/SearchResult";
import SectionHeading from "@/components/styles/SectionHeading";
import Verified from "@/components/hrtk/Verified";
import SearchIcon from "@/components/icons/SearchIcon";
import XIcon from "@/components/icons/XIcon";
import PlusIcon from "@/components/icons/PlusIcon";
import FilterDropdown from "@/components/search/FilterDropdown";
import FacetList from "@/components/search/FacetList";

export default {
    name: "Search",
    components: {
        Loading,
        SearchResult,
        SectionHeading,
        Verified,
        SearchIcon,
        XIcon,
        PlusIcon,
        FilterDropdown,
        FacetList
    },
    data: function() {
        return {
            queryExists: false,
            searchQuery: null,
            hasSearched: false,
            selectedFacets: [],
            facetsDisplayed: false,
            facetUpdate: 0,
            autoLang: true,
        };
    },
    watch: {
        '$route' (to, from) {
            let query = Object.assign({}, this.$route.query);
            this.$store.dispatch(SEARCH, query);
            this.facetUpdate++;
        }
    },
    computed: {
        ...mapGetters([
            'contentFormats',
            'contentLanguages',
            'searchAvailableFacets',
            'searchAvailableFilters',
            'searchFacets',
            'searchResults',
            'searchResultsCmhr',
            'searchNextPage',
            'searchPreviousPage',
            'searchSelectedFacets',
            'searchSelectedFilters',
            'i18n_strings',
            'isLoading',
            'resultsCount',
        ]),
        isCollection: function () {
            return this.$route.query.name;
        },
        currentLang: function () {
            return document.querySelector('html').getAttribute('lang')
        },
        routeName: function () {
            return this.currentLang == 'en' ? 'search' : 'rechercher';
        },
        resultsCountText: function () {
            if (this.isLoading) return "";
            let currentLang = this.currentLang;
            let strs = {};
            let quotes = {
                fr: {
                    open: "« ",
                    close: " »"
                },
                en: {
                    open: "“",
                    close: "”"
                }
            };
            if (this.searchQuery) {
                strs.no_results = `${this.i18n_strings.no_results_for} ${quotes[currentLang].open}${this.searchQuery}${quotes[currentLang].close} ${this.i18n_strings.were_found}`;
                strs.result = `${this.i18n_strings.result_for} ${quotes[currentLang].open}${this.searchQuery}${quotes[currentLang].close}`;
                strs.results = `${this.i18n_strings.results_for} ${quotes[currentLang].open}${this.searchQuery}${quotes[currentLang].close}`;
            } else {
                strs.no_results = this.i18n_strings.no_results;
                strs.result = this.i18n_strings.result;
                strs.results = this.i18n_strings.results;
            }

            switch (this.resultsCount) {
                case 0:
                    return strs.no_results;
                case 1:
                    return `${this.resultsCount} ${strs.result}`;
                default:
                    return `${this.resultsCount} ${strs.results}`;
            }
        }
    },
    methods: {
        addFacet: function(facet) {
            let query = Object.assign({}, this.$route.query);

            if(facet.category in query) {
                if(typeof(query[facet.category]) === 'string' || query[facet.category] instanceof String) {
                    if(query[facet.category] != facet.facet) {
                        query[facet.category] = [query[facet.category], encodeURI(facet.facet)]
                    }
                } else if(query[facet.category] instanceof Array) {
                    if(query[facet.category].indexOf(facet.facet) == -1) {
                        query[facet.category] = [...query[facet.category], encodeURI(facet.facet)]
                    }
                }
            } else {
                query[facet.category] = encodeURI(facet.facet);
            }


            // set lang to html lang if none selected
            if (!query['languages'] && this.autoLang) {
                let langName = this.currentLang == 'fr' ? 'Français' : 'English';
                query['languages'] = langName;
            }

            this.$router.push({
                name: this.routeName,
                query: query
            }).catch(err => {
                console.error(err);
            })
        },
        clearSearchQuery: function(event) {
            this.queryExists = false;
            this.searchQuery = '';
            this.$refs.searchBox.value = '';

            let query = {};
            // set lang to html lang if none selected
            if (!query['languages'] && this.autoLang) {
                let langName = this.currentLang == 'fr' ? 'Français' : 'English';
                query['languages'] = langName;
            }

            this.$router.replace({
                name: this.routeName,
                query: query
            });
        },
        nextPage: function(event) {
            this.$store.dispatch(SEARCH_PAGE_NEXT)
        },
        previousPage: function(event) {
            this.$store.dispatch(SEARCH_PAGE_PREV)
        },
        removeFacet: function(facet) {
            let query = Object.assign({}, this.$route.query);

            if(query[facet.category] instanceof Array) {
                if(query[facet.category].length > 1) {
                    const i = query[facet.category].indexOf(facet.facet);
                    let f = query[facet.category].splice(0);
                    f.splice(i,1);
                    query[facet.category] = f;
                } else {
                    delete query[facet.category];
                }
            } else {
                delete query[facet.category];
            }

            // set lang to html lang if none selected
            if (!query['languages'] && this.autoLang) {
                let langName = this.currentLang == 'fr' ? 'Français' : 'English';
                query['languages'] = langName;
            }

            this.$router.replace({
                name: this.routeName,
                query: Object.assign({}, query)
            });
        },
        killAutoLang: function (e) {
            this.autoLang = false;
        },
        searchQueryChanged: function(event) {
            const searchQuery = event.target.value;
            this.setSearchQuery(searchQuery);
        },
        setSearchQuery: function(query) {
            this.queryExists = query !== '';

            if (this.queryExists) {
                let routeQ = {
                                q: encodeURI(query)
                            };

                // set lang to html lang if none selected
                if (!query['languages'] && this.autoLang) {
                    let langName = this.currentLang == 'fr' ? 'Français' : 'English';
                    routeQ.languages = langName;
                }

                this.$router.replace({
                    name: this.routeName,
                    query: routeQ
                });

                this.searchQuery = decodeURI(query);
                this.hasSearched = true;
            } else {
                this.clearSearchQuery();
            }
        },
        toggleFacetLists: function(facet) {
            this.facetsDisplayed = !this.facetsDisplayed;
        }
    },
    mounted() {
        if ("q" in this.$route.query) {
            this.setSearchQuery(this.$route.query.q);
        }
        this.$store.dispatch(SEARCH, this.$route.query);
        this.facetUpdate++;
    }
};
</script>
